import * as cheerio from "cheerio";
import { Editor } from "tinymce";

import { store } from "app/store";
import type { TextTemplateListItem } from "services/types/text-templates.type";
import { getDynamicApiUrl } from "utils/api-config.util";
import {
  buildAuthHeaderValue,
  getStoredAuthScheme
} from "utils/auth-header.util";
import { getExtensionFromMimeType } from "utils/blob.utils";

import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "./merge-tags/merge-tags-constants";
import { PATIENT_SELECTED_INVOICE_OPTIONS } from "./merge-tags/merge-tags-options/patient-selected-invoice.utils";
import type { MergeTagOptions } from "./merge-tags/merge-tags-values/merge-tags-values";
import { buildMergeTagOptionsString } from "./merge-tags/merge-tags.utils";
import { handleReplaceWildcards } from "./merge-tags/replace-wildcards-editor.util";
import {
  buildReplaceWildcardToken,
  getWildcardHighlightHtml,
  isWildcardTag,
  REPLACE_TAG_BUILDER_TOKEN,
  REPLACE_TAG_DEFAULT_VALUE_COUNT,
  REPLACE_TAG_MAX_VALUE_COUNT,
  REPLACE_WILDCARD_OPTIONS_DELIMITER
} from "./merge-tags/replace-wildcards.utils";
import { handleTemplatesDropdown } from "./text-templates/templates-autocompleter.util";
import type { TemplateSearcher } from "./text-templates/use-template-search.hook";
import { handleEditorOnClick } from "./word-processor-checklist-utils";
import type { FieldInnerStates } from "./word-processor-field.component";
import { setDataParams } from "./word-processor.slice";

type SetupWordProcessorArgs = {
  editor: Editor;
  showTemplatesDialog: () => void;
  showSignDocumentDialog: () => void;
  onInsertMergeTag?: (mergeTag: string) => string | undefined | null;
  setIsRecording: (val: boolean) => void;
  allowHtml?: boolean;
  allowAlign?: boolean;
  allowAIPromptInterface?: boolean;
  preventLineBreak: boolean;
  onClickMergeTagSignButton?: () => void;
  onClickSelectInvoiceButton?: () => void;
  fieldInnerStatesRef: React.MutableRefObject<FieldInnerStates>;
  textTemplates: TextTemplateListItem[];
  onTemplateInsert: (
    template: TextTemplateListItem,
    editor: Editor
  ) => Promise<void>;
  onPrefetchTemplateText?: (templateId: number) => void;
  searchTemplates?: TemplateSearcher;
  allowImages?: boolean;
  getSaleOptions?: () => MergeTagOptions | undefined;
  mergeTagsValuesEnabled: boolean;
};

export const setupWordProcessor = ({
  editor,
  showTemplatesDialog,
  showSignDocumentDialog,
  onInsertMergeTag,
  setIsRecording,
  allowHtml,
  allowAlign,
  allowAIPromptInterface,
  preventLineBreak,
  onClickMergeTagSignButton,
  onClickSelectInvoiceButton,
  fieldInnerStatesRef,
  textTemplates,
  onTemplateInsert,
  onPrefetchTemplateText,
  searchTemplates,
  allowImages,
  getSaleOptions,
  mergeTagsValuesEnabled
}: SetupWordProcessorArgs) => {
  addFullscreenButton(editor);
  addPreviewButton(editor);
  addTemplatesButton(editor, showTemplatesDialog);
  addSignDocumentButton(editor, showSignDocumentDialog);
  addSpeechToTextButton(editor, setIsRecording);

  if (onInsertMergeTag) {
    handleInsertMergeTag({
      editor,
      getMergeTagValue: onInsertMergeTag,
      allowHtml: Boolean(allowHtml),
      getSaleOptions,
      mergeTagsValuesEnabled
    });
  }
  handleEditorOnClick(
    editor,
    onClickMergeTagSignButton,
    onClickSelectInvoiceButton
  );
  if (preventLineBreak) {
    handlePreventLineBreak(editor);
  }
  handlePageBreakRemoval(editor, fieldInnerStatesRef, allowHtml, allowAlign);
  handleReplaceWildcards(editor);
  handleTemplatesDropdown(
    editor,
    textTemplates,
    onTemplateInsert,
    onPrefetchTemplateText,
    searchTemplates
  );

  if (allowImages && allowHtml) {
    handleImageBestFit(editor);
    handleImageResizeContextMenu(editor);
  }

  editor.on("init", function () {
    if (allowAIPromptInterface) {
      editor.editorContainer.style.paddingBottom = "35px";
    }
  });
};

const emptyFn = () => undefined;
const DEFAULT_KEYBOARD_SHORTCUTS = [
  "meta+b",
  "meta+i",
  "meta+u",
  "meta+k",
  "access+1",
  "access+2",
  "access+3",
  "access+4",
  "access+5",
  "access+6"
];

export const removeDefaultShortcuts = (editor: Editor) => {
  DEFAULT_KEYBOARD_SHORTCUTS.forEach((pattern) => {
    editor.addShortcut(pattern, "", emptyFn);
  });
};

const handlePreventLineBreak = (editor: Editor) => {
  editor.on("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  });
};

const addFullscreenButton = (editor: Editor) => {
  editor.ui.registry.addButton("suno_fullscreen_button", {
    text: "Full Screen…",
    icon: "fullscreen",
    tooltip: "Fullscreen",
    onAction: function (api) {
      editor.execCommand("mceFullScreen");
    }
  });
};

const addPreviewButton = (editor: Editor) => {
  editor.ui.registry.addIcon(
    "suno_preview",
    `<svg width="24" height="24" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="FindInPageIcon"><path d="M20 19.59V8l-6-6H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c.45 0 .85-.15 1.19-.4l-4.43-4.43c-.8.52-1.74.83-2.76.83-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5c0 1.02-.31 1.96-.83 2.75L20 19.59zM9 13c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3-3 1.34-3 3z"></path></svg>`
  );
  editor.ui.registry.addButton("suno_preview_button", {
    text: "Preview…",
    icon: "suno_preview",
    tooltip: "Preview",
    onAction: function (api) {
      editor.execCommand("mcePreview");
    }
  });
};

const extractMergeTagName = (value: string) => {
  if (!value.includes("{{") || !value.includes("}}")) return "";

  const $ = cheerio.load(value);
  const fullText = $.root().text().trim();

  return fullText.startsWith("{{") && fullText.endsWith("}}") ? fullText : "";
};

const getReplaceTagValueFieldName = (index: number) => `value${index}`;

const getReplaceTagDialogData = (
  api: any | undefined,
  valueCount: number
): Record<string, string> => {
  const data = api?.getData?.() ?? {};
  return {
    hint: data.hint ?? "",
    ...Array.from({ length: valueCount }).reduce<Record<string, string>>(
      (acc, _, index) => {
        const field = getReplaceTagValueFieldName(index);
        acc[field] = data[field] ?? "";
        return acc;
      },
      {}
    )
  };
};

const getReplaceTagValues = (data: Record<string, string>, valueCount: number) =>
  Array.from({ length: valueCount })
    .map((_, index) => data[getReplaceTagValueFieldName(index)]?.trim() ?? "")
    .filter(Boolean);

const getReplaceTagValidationError = (
  data: Record<string, string>,
  valueCount: number
) => {
  const hint = data.hint?.trim() ?? "";
  const values = getReplaceTagValues(data, valueCount);
  if (!hint) return "Hint is required.";
  if (hint.includes(REPLACE_WILDCARD_OPTIONS_DELIMITER)) {
    return `Hint cannot include "${REPLACE_WILDCARD_OPTIONS_DELIMITER}".`;
  }
  if (
    values.some((value) =>
      value.includes(REPLACE_WILDCARD_OPTIONS_DELIMITER)
    )
  ) {
    return `Values cannot include "${REPLACE_WILDCARD_OPTIONS_DELIMITER}".`;
  }
  return "";
};

const buildReplaceTagDialogSpec = ({
  data,
  valueCount,
  error,
  onAddValue,
  onSubmit
}: {
  data: Record<string, string>;
  valueCount: number;
  error?: string;
  onAddValue: (api: any) => void;
  onSubmit: (api: any) => void;
}) => ({
  title: "Configure replace tag",
  body: {
    type: "panel",
    items: [
      {
        type: "htmlpanel",
        html: `<p style="margin:0 0 12px;color:#667085;">Create a placeholder clinicians must fill before completing a chart note or signing a report. Add optional values to make it a selectable list.</p>${
          error
            ? `<p style="margin:0 0 12px;color:#d32f2f;font-weight:600;">${error}</p>`
            : ""
        }`
      },
      {
        type: "input",
        name: "hint",
        label: "Hint"
      },
      ...Array.from({ length: valueCount }).map((_, index) => ({
        type: "input",
        name: getReplaceTagValueFieldName(index),
        label: `Value ${index + 1}`
      }))
    ]
  },
  initialData: data,
  buttons: [
    { type: "cancel", text: "Cancel" },
    ...(valueCount < REPLACE_TAG_MAX_VALUE_COUNT
      ? [{ type: "custom", name: "addValue", text: "Add more value" }]
      : []),
    { type: "submit", text: "Insert", primary: true }
  ],
  onAction: (api: any, details: any) => {
    if (details?.name === "addValue") onAddValue(api);
  },
  onSubmit
});

const openConfigureReplaceTagDialog = (
  editor: Editor,
  insertToken: (token: string) => void
) => {
  let valueCount = REPLACE_TAG_DEFAULT_VALUE_COUNT;

  const openOrRedial = (api?: any, error?: string) => {
    const data = getReplaceTagDialogData(api, valueCount);
    const spec = buildReplaceTagDialogSpec({
      data,
      valueCount,
      error,
      onAddValue: (dialogApi) => {
        valueCount = Math.min(valueCount + 1, REPLACE_TAG_MAX_VALUE_COUNT);
        openOrRedial(dialogApi);
      },
      onSubmit: (dialogApi) => {
        const submitData = getReplaceTagDialogData(dialogApi, valueCount);
        const validationError = getReplaceTagValidationError(
          submitData,
          valueCount
        );
        if (validationError) {
          openOrRedial(dialogApi, validationError);
          return;
        }

        insertToken(
          buildReplaceWildcardToken(
            submitData.hint,
            getReplaceTagValues(submitData, valueCount)
          )
        );
        dialogApi.close();
      }
    });

    if (api?.redial) {
      api.redial(spec);
      api.setData?.(data);
      return;
    }
    editor.windowManager.open(spec as any);
  };

  openOrRedial();
};

const handleInsertMergeTag = ({
  getMergeTagValue,
  allowHtml,
  editor,
  getSaleOptions,
  mergeTagsValuesEnabled
}: {
  editor: Editor;
  getMergeTagValue: (mergeTag: string) => string | undefined | null;
  allowHtml: boolean;
  mergeTagsValuesEnabled: boolean;
  getSaleOptions?: () => MergeTagOptions | undefined;
}) => {
  let isInsertingMergeTag = false;

  const insertReplaceTagToken = (token: string) => {
    isInsertingMergeTag = true;
    try {
      editor.execCommand(
        "mceInsertContent",
        false,
        getWildcardHighlightHtml(token)
      );
    } finally {
      isInsertingMergeTag = false;
    }
  };

  editor.on("BeforeExecCommand", (event: any) => {
    if (isInsertingMergeTag) return;

    const value = event.value?.content ?? event.value;

    console.log(value);
    if (
      event.command === "mceInsertContent" &&
      typeof value === "string" &&
      value
    ) {
      console.log(extractMergeTagName(value));
      const tagName = extractMergeTagName(value);
      const showSaleConfigurationDialog =
        tagName ===
          `{{${PATIENT_SELECTED_INVOICE_OPTIONS.DEFINE_PARAMETERS}}}` ||
        (tagName ===
          `{{${PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS}}}` &&
          !getSaleOptions?.());

      if (tagName) {
        if (tagName === REPLACE_TAG_BUILDER_TOKEN) {
          openConfigureReplaceTagDialog(editor, insertReplaceTagToken);
          return false;
        }

        if (isWildcardTag(tagName)) {
          insertReplaceTagToken(tagName);
          return false;
        }

        const tagValue = getMergeTagValue(tagName);
        if (tagValue?.length) {
          isInsertingMergeTag = true;
          try {
            editor.execCommand(
              "mceInsertContent",
              false,
              `${MERGE_TAG_REPLACED_VALUE_PREFIX}${tagValue}`
            );
          } finally {
            isInsertingMergeTag = false;
          }
          return false;
        } else if (showSaleConfigurationDialog) {
          store.dispatch(
            setDataParams({
              id: editor.id,
              openSelectSaleConfigurationDialog: true
            })
          );
          return false;
        } else if (
          tagName ===
            `{{${PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS}}}` &&
          getSaleOptions
        ) {
          isInsertingMergeTag = true;
          try {
            editor.execCommand(
              "mceInsertContent",
              false,
              `{{${PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS}${buildMergeTagOptionsString(getSaleOptions())}}}`
            );
          } finally {
            isInsertingMergeTag = false;
          }
          return false;
        } else if (Boolean(mergeTagsValuesEnabled) && !allowHtml) {
          return false;
        }
      }
    }
  });
};

const addSignDocumentButton = (
  editor: Editor,
  showSignDocumentDialog: () => void
) => {
  editor.ui.registry.addIcon(
    "suno_sign_document",
    `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24" height="24" focusable="false" x="0px" y="0px" viewBox="0 0 122.88 107.95" style="enable-background:new 0 0 122.88 107.95" xml:space="preserve"><style type="text/css"></style><g><path class="st0" d="M82.19,0c1.22-0.01,2.27,0.4,3.18,1.27l13.27,12.7c0.86,0.82,1.28,1.91,1.3,3.11 c0.02,1.22-0.34,2.33-1.18,3.18l-7.06,7.35L72.09,8.67l6.99-7.28C79.94,0.5,80.98,0.02,82.19,0L82.19,0z M20.08,68.29 c2.4-0.74,4.93,0.61,5.67,3c0.74,2.4-0.61,4.93-3,5.67c-6.98,2.14-11.66,6.28-13.21,10.17c-0.41,1.04-0.55,1.99-0.39,2.77 c0.12,0.59,0.48,1.13,1.07,1.56c2.55,1.86,8.04,2.34,17.26,0.1c5.46-1.33,11.31-3.13,16.49-4.73c5.29-1.63,9.91-3.06,13.12-3.66 c5.92-1.1,11.83-1.85,16.6-1.61c6.26,0.31,10.96,2.32,12.57,7.13c1.04,3.11,0.75,5.81,0.49,8.23c-0.09,0.87-0.18,1.68-0.14,2.21 c0,0.02,0.95-0.05,5.53-0.41c5.3-0.42,10.79-2.51,16.21-4.58c2.78-1.06,5.54-2.11,8.67-3.07c2.4-0.73,4.93,0.63,5.65,3.02 c0.73,2.4-0.63,4.93-3.02,5.65c-2.43,0.74-5.24,1.82-8.06,2.89c-6.04,2.3-12.14,4.63-18.74,5.15c-12.07,0.95-14.87-2.46-15.3-7.95 c-0.1-1.29,0.03-2.54,0.17-3.88c0.16-1.5,0.34-3.18-0.07-4.39c-0.18-0.54-1.89-0.82-4.39-0.94c-3.99-0.2-9.22,0.48-14.54,1.47 c-2.65,0.49-7.05,1.85-12.09,3.41c-5.27,1.63-11.22,3.46-17.02,4.87c-12.14,2.95-20.21,1.7-24.73-1.6c-2.54-1.85-4.05-4.27-4.63-7 c-0.54-2.54-0.23-5.28,0.85-8C3.56,77.63,10.33,71.27,20.08,68.29L20.08,68.29z M41.27,48.45L53.3,60.02l-14.71,3.95 c-0.53,0.1-0.74-0.11-0.65-0.6L41.27,48.45L41.27,48.45z M65.87,15.11l19.59,18.87L58.7,62.23l-23.98,6.43 c-0.86,0.17-1.2-0.17-1.06-0.98l5.43-24.31L65.87,15.11L65.87,15.11z"/></g></svg>`
  );
  editor.ui.registry.addButton("suno_sign_document_button", {
    text: "Sign…",
    icon: "suno_sign_document",
    tooltip: "Sign document",
    onAction: function (api) {
      showSignDocumentDialog();
    }
  });
};

const addTemplatesButton = (
  editor: Editor,
  showTemplatesDialog: () => void
) => {
  editor.ui.registry.addIcon(
    "suno_templates",
    `<svg width="24" height="24" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="TextSnippetIcon"><path d="m20.41 8.41-4.83-4.83c-.37-.37-.88-.58-1.41-.58H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.83c0-.53-.21-1.04-.59-1.42zM7 7h7v2H7V7zm10 10H7v-2h10v2zm0-4H7v-2h10v2z"></path></svg>`
  );
  editor.ui.registry.addButton("suno_templates_button", {
    text: "Templates…",
    icon: "suno_templates",
    tooltip: "Insert text from template",
    onAction: function (api) {
      showTemplatesDialog();
    }
  });
};

export const addSpeechToTextButton = (
  editor: Editor,
  setIsRecording: (val: boolean) => void
) => {
  editor.ui.registry.addIcon(
    "recording_on",
    `<svg width="24" height="24" focusable="false" style="fill:#d72c2c" aria-hidden="true" viewBox="0 0 24 24" data-testid="KeyboardVoiceIcon"><path d="M12 15c1.66 0 2.99-1.34 2.99-3L15 6c0-1.66-1.34-3-3-3S9 4.34 9 6v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 15 6.7 12H5c0 3.42 2.72 6.23 6 6.72V22h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"></path></svg>`
  );

  editor.ui.registry.addIcon(
    "recording_off",
    `<svg width="24" height="24" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="KeyboardVoiceIcon"><path d="M12 15c1.66 0 2.99-1.34 2.99-3L15 6c0-1.66-1.34-3-3-3S9 4.34 9 6v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 15 6.7 12H5c0 3.42 2.72 6.23 6 6.72V22h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"></path></svg>`
  );

  editor.ui.registry.addToggleButton("speech_to_text_button", {
    icon: "recording_off",
    tooltip: "Speech to text",
    onSetup: function (api) {
      const handleReset = () => {
        api.setActive(false);
        api.setIcon("recording_off");
      };
      editor.on("SpeechToTextReset", handleReset);

      return () => {
        editor.off("SpeechToTextReset", handleReset);
      };
    },
    onAction: function (api) {
      const isActive = !api.isActive();
      api.setActive(isActive);

      if (isActive) {
        api.setIcon("recording_on");
      } else {
        api.setIcon("recording_off");
      }
      setIsRecording(isActive);
    }
  });
};

export const resetSpeechToTextButton = (editor: Editor) => {
  editor.dispatch("SpeechToTextReset");
};

export const filePickerCallback = (
  editorRef: any,
  cb: any,
  maxImageSizeMB?: number
) => {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");

  input.addEventListener("change", (e: any) => {
    const file = e?.target?.files[0];

    if (!file) return;

    if (
      maxImageSizeMB &&
      maxImageSizeMB > 0 &&
      file.size > maxImageSizeMB * 1024 * 1024
    ) {
      alert(`File is too large. Max size is ${maxImageSizeMB} MB`);
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const id = "blobid" + new Date().getTime();
      const blobCache = editorRef.current?.editorUpload.blobCache;
      const base64 = String(reader.result ?? "").split(",")[1];
      const blobInfo: any = blobCache?.create(id, file, base64);
      blobCache?.add(blobInfo);
      cb(blobInfo?.blobUri(), { title: file.name });
    });
    reader?.readAsDataURL(file);
  });

  input.click();
};

export const imagesUploadHandler = async (params: {
  token: string;
  autoUpload: boolean;
  blobInfo: any;
  progress: (percent: number) => void;
  maxImageSizeMB?: number;
}): Promise<string> => {
  const { token, autoUpload, blobInfo, progress, maxImageSizeMB } = params;
  return new Promise((resolve, reject) => {
    if (!blobInfo || typeof blobInfo.blob !== "function") {
      return reject({
        message: "Invalid image data",
        remove: true
      });
    }

    const file = blobInfo.blob();

    if (
      maxImageSizeMB &&
      maxImageSizeMB > 0 &&
      file.size > maxImageSizeMB * 1024 * 1024
    ) {
      return reject({
        message: `File is too large. Max size is ${maxImageSizeMB} MB`,
        remove: true
      });
    }

    if (!autoUpload) {
      progress(100);

      try {
        const base64 = blobInfo.base64();
        const mime = file.type;
        resolve(`data:${mime};base64,${base64}`);
      } catch (error) {
        reject(new Error("Failed to convert image to base64"));
      }
      return;
    }

    const extension = getExtensionFromMimeType(file);

    const url = `${getDynamicApiUrl()}/emails/upload-file/`;

    const xhr = new XMLHttpRequest();
    xhr.withCredentials = false;
    xhr.open("POST", url);
    xhr.setRequestHeader(
      "Authorization",
      buildAuthHeaderValue(token, getStoredAuthScheme())
    );

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = (e.loaded / e.total) * 100;
        try {
          progress(percent);
        } catch {
          /* editor may be destroyed */
        }
      }
    };

    xhr.onload = () => {
      if (xhr.status === 403) {
        reject({ message: "HTTP Error: " + xhr.status, remove: true });
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject({ message: "HTTP Error: " + xhr.status });
        return;
      }

      const json = JSON.parse(xhr.responseText);

      if (!json || typeof json.url != "string") {
        reject({ message: xhr.responseText });
        return;
      }

      resolve(json.url);
    };

    xhr.onerror = () => {
      reject({
        message:
          "Image upload failed due to a XHR Transport error. Code: " +
          xhr.status,
        remove: true
      });
    };

    const formData = new FormData();
    formData.append("content", blobInfo.blob(), `file.${extension}`);
    formData.append("extension", extension);

    xhr.send(formData);
  });
};

const handlePageBreakRemoval = (
  editor: Editor,
  fieldInnerStatesRef: React.MutableRefObject<FieldInnerStates>,
  allowHtml: boolean | undefined,
  allowAlign: boolean | undefined
) => {
  editor.ui.registry.addButton("deletePagebreak", {
    icon: "remove",
    tooltip: "Delete Page Break",
    onAction: () => {
      const node = editor.selection.getNode();
      if (node && node.classList.contains("mce-pagebreak")) {
        if (fieldInnerStatesRef.current.pageBreakSelected) {
          fieldInnerStatesRef.current.pageBreakSelected = undefined;
        }
        editor.dom.remove(node);
        editor.selection.collapse(false);
      }
    }
  });

  editor.ui.registry.addContextToolbar("pagebreakToolbar", {
    predicate: (node) => node?.classList?.contains("mce-pagebreak"),
    items: "deletePagebreak",
    position: "node",
    scope: "node"
  });

  editor.ui.registry.addContextToolbar("textToolbar", {
    predicate: () => {
      const node = editor.selection.getNode();
      const isReplaceWildcard = Boolean(
        node.classList?.contains("replace-wildcard") ||
          node.closest?.(".replace-wildcard")
      );

      return (
        !isReplaceWildcard &&
        !editor.selection.isCollapsed() &&
        !node.classList?.contains("mce-pagebreak")
      );
    },
    items: `${allowHtml && allowAlign ? "align" : ""} ${
      allowHtml
        ? "| blocks fontsizeinput | bold italic underline forecolor backcolor | removeformat"
        : ""
    }`,
    position: "selection",
    scope: "node"
  });

  editor.on("ObjectSelected", (event: any) => {
    const target = event.target;
    if (target?.classList?.contains("mce-pagebreak")) {
      fieldInnerStatesRef.current.pageBreakSelected = target;
    }
  });

  editor.on("SelectionChange", (event: any) => {
    if (fieldInnerStatesRef.current.pageBreakSelected) {
      fieldInnerStatesRef.current.pageBreakSelected = undefined;
    }
  });

  editor.on("keydown", (event: any) => {
    if (event.code === "Backspace" || event.key === "Backspace") {
      if (fieldInnerStatesRef.current.pageBreakSelected) {
        editor.dom.remove(fieldInnerStatesRef.current.pageBreakSelected);
        editor.selection.collapse(false);
        fieldInnerStatesRef.current.pageBreakSelected = undefined;
      }
    }
  });
};

// Store initial container height to avoid it changing when image grows
const containerHeights = new WeakMap<Editor, number>();

const getBestFitDimensions = (
  editor: Editor
): { width: number; height: number } => {
  const body = editor.getBody();
  const bodyWidth = body.clientWidth || 600;

  // Get or store the initial container height
  let containerHeight = containerHeights.get(editor);

  if (!containerHeight) {
    // First time - get the initial container height
    const container = editor.getContainer();
    const contentAreaContainer = editor.getContentAreaContainer();
    containerHeight =
      container?.clientHeight || contentAreaContainer?.clientHeight || 400;

    // If we have a configured height, use that instead
    const configuredHeight = (editor as any).settings?.height;
    if (configuredHeight && typeof configuredHeight === "number") {
      containerHeight = configuredHeight;
    }

    containerHeights.set(editor, containerHeight);
  }

  // Use viewport height as additional constraint (80% of viewport as max)
  const viewportHeight = window.innerHeight;
  const maxVisibleHeight = Math.min(containerHeight, viewportHeight * 0.8);

  const padding = 20;
  const result = {
    width: bodyWidth - padding * 2,
    height: maxVisibleHeight - padding * 2
  };
  return result;
};

const applyBestFitSize = (editor: Editor, img: HTMLImageElement) => {
  const bestFitDimensions = getBestFitDimensions(editor);

  // Always use stored original dimensions first, then natural dimensions
  // Never use img.width/img.height as they may be modified
  let originalWidth: number;
  let originalHeight: number;

  const storedWidth = parseInt(img.getAttribute("data-original-width") || "0");
  const storedHeight = parseInt(
    img.getAttribute("data-original-height") || "0"
  );

  if (storedWidth > 0 && storedHeight > 0) {
    // Use stored original dimensions
    originalWidth = storedWidth;
    originalHeight = storedHeight;
  } else if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    // Use natural dimensions and store them
    originalWidth = img.naturalWidth;
    originalHeight = img.naturalHeight;
    img.setAttribute("data-original-width", originalWidth.toString());
    img.setAttribute("data-original-height", originalHeight.toString());
  } else {
    // Can't determine original size, return early
    return;
  }

  // Calculate scale ratios for both width and height
  const widthRatio = bestFitDimensions.width / originalWidth;
  const heightRatio = bestFitDimensions.height / originalHeight;

  // Use the smaller ratio to ensure image fits both dimensions
  // Don't scale up if image is smaller than available space
  const scaleRatio = Math.min(widthRatio, heightRatio, 1);

  if (scaleRatio >= 1) {
    // Image is smaller than best fit, keep original size
    img.style.width = "";
    img.style.height = "";
    img.setAttribute("width", originalWidth.toString());
    img.setAttribute("height", originalHeight.toString());
  } else {
    // Scale down to best fit
    const newWidth = originalWidth * scaleRatio;
    const newHeight = originalHeight * scaleRatio;
    img.style.width = `${newWidth}px`;
    img.style.height = `${newHeight}px`;
    img.setAttribute("width", Math.round(newWidth).toString());
    img.setAttribute("height", Math.round(newHeight).toString());
  }

  // Mark as best fit
  img.setAttribute("data-image-size", "best-fit");
};

const processImageForBestFit = (editor: Editor, img: HTMLImageElement) => {
  if (img.getAttribute("data-image-size")) {
    return;
  }

  const processImage = () => {
    if (img.naturalWidth && img.naturalHeight) {
      applyBestFitSize(editor, img);
    } else {
      const onLoad = () => {
        applyBestFitSize(editor, img);
        img.removeEventListener("load", onLoad);
      };
      img.addEventListener("load", onLoad);

      if (img.complete) {
        setTimeout(() => {
          if (img.naturalWidth && img.naturalHeight) {
            applyBestFitSize(editor, img);
          }
        }, 0);
      }
    }
  };

  processImage();
};

const handleImageBestFit = (editor: Editor) => {
  editor.on("PastePostProcess", (e: any) => {
    setTimeout(() => {
      const body = editor.getBody();
      const images = body?.querySelectorAll("img");
      images?.forEach((img: HTMLImageElement) => {
        processImageForBestFit(editor, img);
      });
    }, 0);
  });

  editor.on("drop", (e: any) => {
    setTimeout(() => {
      const body = editor.getBody();
      const images = body?.querySelectorAll("img");
      images?.forEach((img: HTMLImageElement) => {
        processImageForBestFit(editor, img);
      });
    }, 100);
  });

  editor.on("SetContent", (e: any) => {
    if (e.content && e.content.includes("<img")) {
      setTimeout(() => {
        const body = editor.getBody();
        const images = body?.querySelectorAll("img");
        images?.forEach((img: HTMLImageElement) => {
          processImageForBestFit(editor, img);
        });
      }, 0);
    }
  });

  editor.on("ObjectResized", (e: any) => {
    if (e.target && e.target.tagName === "IMG") {
      const img = e.target as HTMLImageElement;
      processImageForBestFit(editor, img);
    }
  });

  editor.on("ExecCommand", (e: any) => {
    if (
      e.command === "mceInsertContent" &&
      typeof e.value === "string" &&
      e.value.indexOf("<img") !== -1
    ) {
      setTimeout(() => {
        const body = editor.getBody();
        const images = body?.querySelectorAll("img");
        images?.forEach((img: HTMLImageElement) => {
          processImageForBestFit(editor, img);
        });
      }, 0);
    }
  });
};

const handleImageResizeContextMenu = (editor: Editor) => {
  const getSelectedImage = (): HTMLImageElement | null => {
    const node = editor.selection.getNode();
    if (node && node.tagName === "IMG") {
      return node as HTMLImageElement;
    }
    return null;
  };

  const setImageSize = (size: "small" | "best-fit" | "original") => {
    const img = getSelectedImage();
    if (!img) return;

    // Ensure we have original dimensions stored
    // Always use naturalWidth/naturalHeight, never use img.width/img.height as they may be modified
    if (!img.getAttribute("data-original-width")) {
      if (img.naturalWidth && img.naturalHeight) {
        img.setAttribute("data-original-width", img.naturalWidth.toString());
        img.setAttribute("data-original-height", img.naturalHeight.toString());
      } else {
        // Can't determine original size without natural dimensions
        return;
      }
    }

    // Always use stored original dimensions or natural dimensions
    // Never use img.width/img.height as fallback
    const storedWidth = parseInt(
      img.getAttribute("data-original-width") || "0"
    );
    const storedHeight = parseInt(
      img.getAttribute("data-original-height") || "0"
    );

    const originalWidth = storedWidth > 0 ? storedWidth : img.naturalWidth || 0;
    const originalHeight =
      storedHeight > 0 ? storedHeight : img.naturalHeight || 0;

    if (originalWidth <= 0 || originalHeight <= 0) {
      // Can't determine original size
      return;
    }

    switch (size) {
      case "small":
        const smallWidth = 200;
        const smallHeight =
          originalWidth > 0
            ? (originalHeight / originalWidth) * smallWidth
            : smallWidth;
        img.style.width = `${smallWidth}px`;
        img.style.height = `${Math.round(smallHeight)}px`;
        img.setAttribute("width", smallWidth.toString());
        img.setAttribute("height", Math.round(smallHeight).toString());
        img.setAttribute("data-image-size", "small");
        break;
      case "best-fit":
        applyBestFitSize(editor, img);
        break;
      case "original":
        // Use stored original dimensions
        img.style.width = "";
        img.style.height = "";
        img.setAttribute("width", originalWidth.toString());
        img.setAttribute("height", originalHeight.toString());
        img.setAttribute("data-image-size", "original");
        break;
      default:
        return;
    }

    editor.nodeChanged();
  };

  editor.ui.registry.addContextToolbar("imageToolbar", {
    predicate: (node) => {
      return node.tagName === "IMG";
    },
    items:
      "imageSizeSmall imageSizeBestFit imageSizeOriginal imageSizeCustom | image | remove",
    position: "node",
    scope: "node"
  });

  editor.ui.registry.addButton("imageSizeSmall", {
    text: "Small",
    tooltip: "Small",
    onAction: () => setImageSize("small")
  });

  editor.ui.registry.addButton("imageSizeBestFit", {
    text: "Best fit",
    tooltip: "Best fit",
    onAction: () => setImageSize("best-fit")
  });

  editor.ui.registry.addButton("imageSizeOriginal", {
    text: "Original size",
    tooltip: "Original size",
    onAction: () => setImageSize("original")
  });
};
